// auftrag writes companies, addresses, mails and persons directly (AB5) and reports milestones into the history (AB6),
// operator's decision of 03.11.2026 (docs/schnittstelle-adressbuch.md 7). The fields are AB4's, plus the name parts and
// position of a person, the customer number of a company and the mails of a company.
import { ADDRESS_ARTEN, CHANGE_FIELDS, type ChangeField } from "./change-requests";

export type AuftragTarget = "firma" | "person" | "adresse" | "email";

/** A field auftrag writes, with the column it lands in and how it is checked. */
export interface AuftragField extends Omit<ChangeField, "kind"> {
  kind?: ChangeField["kind"] | "kundennummer" | "zweck";
}

const { name: _displayName, ...personOhneName } = CHANGE_FIELDS.person;

export const AUFTRAG_FIELDS: Record<AuftragTarget, Record<string, AuftragField>> = {
  firma: { ...CHANGE_FIELDS.firma, kundennummer: { column: "customer_number", label: "Kundennummer", kind: "kundennummer" } },
  // The display name follows from title, first and last name; it is not written on its own.
  person: {
    ...personOhneName,
    titel: { column: "title", label: "Titel" },
    vorname: { column: "first_name", label: "Vorname" },
    nachname: { column: "last_name", label: "Nachname" },
    position: { column: "role", label: "Position" },
  },
  adresse: CHANGE_FIELDS.adresse,
  email: {
    zweck: { column: "purpose", label: "Zweck", kind: "zweck" },
    email: { column: "email", label: "Mail", kind: "email" },
    bezeichnung: { column: "label", label: "Bezeichnung" },
  },
};

/** What a new record must bring; a person needs a first or a last name (checked on its own). */
export const AUFTRAG_NEW_FIELDS: Record<AuftragTarget, string[]> = { firma: ["name"], person: [], adresse: ["art", "strasse", "plz", "ort"], email: ["zweck", "email"] };

/** The customer number auftrag gives: K-JJ-XXXX since auftrag's switch of 03.11.2026, the former K-JJJJ-XXXX still taken. */
export const KUNDENNUMMER = /^K-(\d{2}|\d{4})-[A-Z0-9]{4}$/;

const NOUN: Record<AuftragTarget, string> = { firma: "eine Firma", person: "eine Person", adresse: "eine Adresse", email: "eine Mailadresse" };
const MAX = 300;

export type AuftragCheck = { ok: true; changes: Record<string, string | null> } | { ok: false; feld: string | null; message: string };

/**
 * The fields auftrag sends, checked and normalised like AB4: text or null (empty is null), the USt-IdNr. without spaces
 * in upper case; `anschrift` of a company may come as an object (anschrift.<feld>). Fields not named here are refused,
 * so a typo never passes unnoticed. `creates`: a new record, its required fields must be there.
 */
export function readAuftragFields(target: AuftragTarget, raw: Record<string, unknown>, creates: boolean): AuftragCheck {
  const bad = (feld: string | null, message: string): AuftragCheck => ({ ok: false, feld, message });
  const entries = Object.entries(raw).flatMap(([k, v]): [string, unknown][] =>
    target === "firma" && k === "anschrift" && v && typeof v === "object" && !Array.isArray(v) ? Object.entries(v).map(([s, w]) => [`anschrift.${s}`, w]) : [[k, v]],
  );
  const defs = AUFTRAG_FIELDS[target];
  const changes: Record<string, string | null> = {};
  for (const [feld, v] of entries) {
    const def = Object.hasOwn(defs, feld) ? defs[feld]! : null;
    if (!def) return bad(feld, `Das Feld „${feld}“ gibt es für ${NOUN[target]} nicht.`);
    if (v !== null && typeof v !== "string") return bad(feld, `Feld „${feld}“: Text oder null erwartet.`);
    let value = v?.trim() || null;
    if (value && value.length > MAX) return bad(feld, `Feld „${feld}“: höchstens ${MAX} Zeichen.`);
    if (def.kind === "vat" && value) value = value.replace(/\s+/g, "").toUpperCase();
    if (def.kind === "email" && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return bad(feld, `Feld „${feld}“: keine gültige Mailadresse.`);
    if (def.kind === "art" && !ADDRESS_ARTEN.includes(value as (typeof ADDRESS_ARTEN)[number])) return bad(feld, `Feld „${feld}“: rechnung, lieferung oder weitere erwartet.`);
    if (def.kind === "zweck" && value !== "rechnung" && value !== "bestellung") return bad(feld, `Feld „${feld}“: rechnung oder bestellung erwartet.`);
    if (def.kind === "kundennummer" && value && !KUNDENNUMMER.test(value)) return bad(feld, `Feld „${feld}“: Kundennummer der Form K-JJ-XXXX erwartet.`);
    if ((def.kind === "required" || (target === "email" && feld === "email")) && !value) return bad(feld, `Feld „${feld}“ darf nicht leer sein.`);
    changes[feld] = value;
  }
  if (creates) {
    const missing = AUFTRAG_NEW_FIELDS[target].find((f) => !changes[f]);
    if (missing) return bad(missing, `Für ${NOUN[target]} fehlt das Feld „${missing}“.`);
  }
  return { ok: true, changes };
}

/** The display name of a person from its parts: „Dr. Jana Fischer“; null when none is given. */
export const personName = (titel: string | null | undefined, vorname: string | null | undefined, nachname: string | null | undefined): string | null =>
  [titel, vorname, nachname].map((x) => x?.trim()).filter(Boolean).join(" ") || null;

/** Milestones auftrag reports into the history (AB6). */
export const EREIGNIS_ARTEN = [
  "kunde_angelegt",
  "vertrag_unterschrieben",
  "portalzugang_eingeladen",
  "portalzugang_aktiv",
  "portalzugang_gesperrt",
  "auftrag_eingegangen",
  "auftrag_bestaetigt",
  "auftrag_storniert",
] as const;
export type EreignisArt = (typeof EREIGNIS_ARTEN)[number];

export const EREIGNIS_LABELS: Record<EreignisArt, string> = {
  kunde_angelegt: "Kunde angelegt",
  vertrag_unterschrieben: "Vertrag unterschrieben",
  portalzugang_eingeladen: "Portalzugang eingeladen",
  portalzugang_aktiv: "Portalzugang aktiv",
  portalzugang_gesperrt: "Portalzugang gesperrt",
  auftrag_eingegangen: "Auftrag eingegangen",
  auftrag_bestaetigt: "Auftrag bestätigt",
  auftrag_storniert: "Auftrag storniert",
};

/** Links of milestones lead into auftrag, nowhere else. */
export const AUFTRAG_LINK = "https://auftrag.valphira.com/";
